import type { PerformanceSummaryListItem } from '../types.js'
import { formatWhen, personLabel, spanLabel } from '../labels.js'

interface SummaryListProps {
  summaries: readonly PerformanceSummaryListItem[]
  empty: string
  onOpen: (summaryId: string) => void
  /** Set when a row offers its CSV straight away. */
  onDownload?: (summaryId: string) => void
  /** The summary whose CSV is downloading, so its button waits. */
  downloading?: string | null
  /** Releases a summary to the worker's own app, or withdraws it; absent where the reader may not (the admin). */
  onToggleRelease?: (summaryId: string, release: boolean) => void
  /** The summary whose release is changing. */
  releasing?: string | null
}

/** A worker's summaries, newest first: when, for which months, by whom, and a way to open each. */
export function SummaryList({ summaries, empty, onOpen, onDownload, downloading = null, onToggleRelease, releasing = null }: SummaryListProps) {
  if (summaries.length === 0) {
    return <p className="text-[11px] text-ink-mid">{empty}</p>
  }
  return (
    <ul className="divide-y divide-marine/10 border border-marine/15 bg-white text-xs">
      {summaries.map((item) => (
        <li key={item.summaryId} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2">
          <span className="font-bold text-ink">{spanLabel(item.span)}</span>
          <span className="text-ink-mid">
            {formatWhen(item.generatedAt)} · {personLabel(item.generatedBy)}
          </span>
          {item.note && <span className="text-ink-mid italic truncate max-w-xs" title={item.note}>“{item.note}”</span>}
          {item.release ? (
            <span className="rounded-full bg-green-lt px-2 py-0.5 text-[10px] font-bold text-green" title={`Released to the worker ${formatWhen(item.release.releasedAt)} by ${personLabel(item.release.releasedBy)}`}>
              Worker can see this
            </span>
          ) : (
            <span className="rounded-full bg-canvas px-2 py-0.5 text-[10px] font-bold text-ink-mid">Not released</span>
          )}
          <span className="ml-auto flex items-center gap-3">
            <button type="button" onClick={() => onOpen(item.summaryId)} aria-label={`Open the summary for ${spanLabel(item.span)}`} className="text-[11px] font-bold text-marine hover:text-marine-dk">
              Open
            </button>
            {onToggleRelease && (
              <button
                type="button"
                onClick={() => onToggleRelease(item.summaryId, !item.release)}
                disabled={releasing !== null}
                aria-label={`${item.release ? 'Withdraw' : 'Release'} the summary for ${spanLabel(item.span)} ${item.release ? 'from' : 'to'} the worker`}
                className="text-[11px] font-bold text-marine hover:text-marine-dk disabled:opacity-50"
              >
                {releasing === item.summaryId ? 'Saving…' : item.release ? 'Withdraw' : 'Release to worker'}
              </button>
            )}
            {onDownload && (
              <button
                type="button"
                onClick={() => onDownload(item.summaryId)}
                disabled={downloading !== null}
                aria-label={`Download the CSV of the summary for ${spanLabel(item.span)}`}
                className="text-[11px] font-bold text-marine hover:text-marine-dk disabled:opacity-50"
              >
                {downloading === item.summaryId ? 'Saving…' : 'CSV'}
              </button>
            )}
          </span>
        </li>
      ))}
    </ul>
  )
}
